<template>
    <div class="iyb-traffic-light-panel">
  
      <div v-if="trafficLight && conditionsDocUrl" class="conditions-doc-link mb-3">
        <v-icon small color="primary" class="mr-1">mdi-file-document-outline</v-icon>
        <a
          :href="effectiveConditionsDocUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="conditions-doc-link__anchor"
        >
          {{ tp('viewAssessmentConditions') }}
          <v-icon x-small class="ml-1">mdi-open-in-new</v-icon>
        </a>
        <span class="text-caption text--secondary ml-1">{{ conditionsDocLabel }}</span>
      </div>
  
      <!-- Pending state -->
      <div v-if="!trafficLight" class="pa-3 d-flex align-center"
        style="background:#f5f5f5; border:1px dashed #ccc; border-radius:10px;">
        <v-icon color="grey lighten-1" class="mr-3">mdi-radar</v-icon>
        <div>
          <div class="text-body-2 font-weight-medium grey--text text--darken-1">{{ tp('landAssessmentNotYetRun') }}</div>
          <div class="text-caption grey--text">
            {{ tp('landAssessmentNotYetRunDesc') }}
          </div>
        </div>
      </div>
  
      <!-- Phase sections (only when assessed) -->
      <template v-if="trafficLight">
  
        <!-- Phase 1: Spatial Check (coming soon for now) -->
        <div class="phase-card mb-3" :style="phaseTopBorderStyle(spatialPhaseBorderColor)">
          <div class="phase-card__header d-flex align-center justify-space-between mb-3">
            <div class="d-flex align-center">
              <div class="phase-step-badge mr-2" :style="phaseStepBadgeStyle(spatialPhaseBorderColor)">1</div>
              <div>
                <div class="text-caption font-weight-bold text-uppercase phase-card__title">{{ tp('landPolygonAssessment') }}</div>
                <div class="text-caption text--secondary">{{ tp('landPolygonAssessmentDesc') }}</div>
              </div>
            </div>
            <div class="phase-summary-pill phase-summary-pill--upcoming">
              <v-icon x-small color="#607D8B" class="mr-1">mdi-progress-clock</v-icon>
              <span class="text-caption font-weight-bold">{{ $t('comingSoon') }}</span>
            </div>
            <div v-if="false" class="d-flex align-center phase-summary-pill" :class="phase1SummaryClass">
              <v-progress-circular
                :value="phase1ClearPercent"
                :color="phase1SummaryColor"
                size="36"
                width="4"
                class="mr-2"
              >
                <span class="text-caption font-weight-bold" style="font-size:10px;">{{ phase1ClearCount }}/3</span>
              </v-progress-circular>
              <span class="text-caption font-weight-bold">{{ phase1SummaryLabel }}</span>
            </div>
          </div>
  
          <!-- Upcoming feature — static preview (no live map / spinner) -->
          <div class="phase-upcoming-feature">
            <div class="phase-upcoming-feature__preview">
              <div class="phase-upcoming-feature__map">
                <div class="phase-upcoming-feature__map-grid" aria-hidden="true"></div>
                <div class="phase-upcoming-feature__map-content">
                  <v-icon size="36" color="#B0BEC5">mdi-map-search-outline</v-icon>
                  <span v-if="farm && farm.farmName" class="phase-upcoming-feature__farm-name">{{ farm.farmName }}</span>
                </div>
                <div class="phase-upcoming-feature__legend">
                  <span v-for="item in phase1LegendPreview" :key="item.key" class="phase-upcoming-feature__legend-item">
                    <span class="phase-upcoming-feature__legend-swatch" :style="{ background: item.color }"></span>
                    {{ item.label }}
                  </span>
                </div>
              </div>
              <div class="phase-upcoming-feature__tiles">
                <div
                  v-for="check in spatialChecks"
                  :key="check.key"
                  class="phase-upcoming-feature__tile"
                >
                  <v-icon size="20" color="#B0BEC5">{{ check.icon }}</v-icon>
                  <span class="phase-upcoming-feature__tile-label">{{ check.shortLabel }}</span>
                </div>
              </div>
            </div>
            <div class="phase-upcoming-feature__overlay">
              <div class="phase-upcoming-feature__card">
                <span class="phase-upcoming-feature__pill">{{ $t('comingSoon') }}</span>
                <div class="phase-upcoming-feature__title">{{ tp('landPolygonAssessment') }}</div>
                <div class="phase-upcoming-feature__desc">
                  {{ tp('landPolygonAssessmentPreviewDesc') }}
                </div>
              </div>
            </div>
          </div>
  
          <!-- Live Phase 1 disabled until polygon checks are enabled -->
          <div v-if="false" class="phase-card__body-wrap">
            <TrafficLightSpatialMap
              class="mb-3"
              :farm="farm"
              :farm-type="farmType"
              :phase1="phase1"
              view-mode="farm"
              :active="mapActive"
              :height="220"
            />
  
            <div class="spatial-check-grid">
              <v-tooltip
                v-for="check in spatialChecks"
                :key="check.key"
                bottom
                color="black"
                max-width="320"
                open-on-hover
              >
                <template v-slot:activator="{ on, attrs }">
                  <div
                    v-bind="attrs"
                    v-on="on"
                    class="spatial-check-tile spatial-check-tile--tooltip"
                    :class="check.tileClass"
                  >
                    <div class="spatial-check-tile__icon-wrap" :style="{ background: check.iconBg }">
                      <v-icon :color="check.iconColor" size="26">{{ check.icon }}</v-icon>
                    </div>
                    <div class="spatial-check-tile__status-dot" :style="{ background: check.dotColor }"></div>
                    <div class="spatial-check-tile__title">{{ check.shortLabel }}</div>
                    <div class="spatial-check-tile__chip" :style="{ color: check.chipColor, background: check.chipBg }">
                      <v-icon x-small class="mr-1">{{ check.statusIcon }}</v-icon>
                      {{ check.chipLabel }}
                    </div>
                    <div class="spatial-check-tile__hint">{{ check.hint }}</div>
                  </div>
                </template>
                <div style="font-size:12px; line-height:1.65;">
                  <div class="font-weight-bold mb-1">{{ check.tooltipTitle }}</div>
                  <div class="mb-2" style="opacity:0.85;">{{ check.tooltipStatus }}</div>
                  <div>{{ check.tooltipDetail }}</div>
                </div>
              </v-tooltip>
            </div>
          </div>
        </div>
  
        <!-- Phase connector -->
        <div class="phase-connector mb-3">
          <div class="phase-connector__line"></div>
          <v-icon small color="grey lighten-1">mdi-chevron-down</v-icon>
          <div class="phase-connector__line"></div>
        </div>
  
        <!-- Phase 2: IYB Regional Score -->
        <div class="phase-card mb-3" :style="phaseTopBorderStyle(iybPhaseBorderColor)">
          <div class="phase-card__header d-flex align-center justify-space-between mb-3">
            <div class="d-flex align-center">
              <div class="phase-step-badge mr-2" :style="phaseStepBadgeStyle(iybPhaseBorderColor)">2</div>
              <div>
                <div class="text-caption font-weight-bold text-uppercase phase-card__title">{{ tp('districtAssessment') }}</div>
                <div class="text-caption text--secondary">{{ tp('districtAssessmentDesc') }}</div>
              </div>
            </div>
            <v-btn
              v-if="hasIndicatorDetails"
              x-small text
              color="primary"
              @click="showBreakdown = !showBreakdown"
            >
              <v-icon x-small class="mr-1">{{ showBreakdown ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              {{ showBreakdown ? tp('hideIndicators') : tp('showIndicators') }}
            </v-btn>
          </div>
  
          <div v-if="iybData" class="iyb-visual-panel">
            <div class="d-flex align-center justify-space-between mb-3">
              <div class="d-flex align-center">
                <div class="regency-pin mr-2">
                  <v-icon small color="primary">mdi-map-marker</v-icon>
                </div>
                <div>
                  <div class="text-caption text--secondary">{{ $t('dueDiligence.iyb.regencyKabupaten') }}</div>
                  <div class="text-body-2 font-weight-bold">{{ iybData.regencyName || tp('unknownRegency') }}</div>
                </div>
              </div>
              <v-tooltip bottom color="black" max-width="340" open-on-hover>
                <template v-slot:activator="{ on, attrs }">
                  <div
                    v-bind="attrs"
                    v-on="on"
                    class="iyb-risk-badge iyb-risk-badge--tooltip"
                    :class="getRiskClass(iybComputedRiskStatus)"
                  >
                    <v-icon x-small class="mr-1">{{ getRiskIcon(iybComputedRiskStatus) }}</v-icon>
                    {{ iybComputedRiskStatusLabel }}
                  </div>
                </template>
                <div style="font-size:12px; line-height:1.65;">
                  <div class="font-weight-bold mb-1">{{ tp('localComplianceCheckIyb') }}</div>
                  <div class="mb-2" style="opacity:0.85;">{{ iybComplianceTooltipSummary }}</div>
                  <ul class="iyb-tooltip-checkpoints mb-0 pl-3">
                    <li v-for="(line, i) in iybComplianceTooltipCheckpoints" :key="i">{{ line }}</li>
                  </ul>
                </div>
              </v-tooltip>
            </div>
  
            <div v-if="iybScore !== null" class="iyb-score-visual">
              <div class="iyb-gauge-wrap">
                <svg class="iyb-gauge" viewBox="0 0 200 132" aria-hidden="true">
                  <path
                    v-for="zone in iybGaugeZones"
                    :key="zone.key"
                    class="iyb-gauge__zone"
                    :d="zone.d"
                    :stroke="zone.color"
                  />
                  <g v-for="tick in iybGaugeTicks" :key="'tick-' + tick.value">
                    <line
                      class="iyb-gauge__tick"
                      :x1="tick.x1" :y1="tick.y1"
                      :x2="tick.x2" :y2="tick.y2"
                    />
                    <text
                      class="iyb-gauge__tick-label"
                      :x="tick.lx" :y="tick.ly"
                      text-anchor="middle"
                    >{{ tick.label }}</text>
                  </g>
                  <line
                    class="iyb-gauge__needle"
                    x1="100" y1="100"
                    :x2="iybGaugeNeedle.x" :y2="iybGaugeNeedle.y"
                    :stroke="getRiskScoreColor(iybScore)"
                  />
                  <circle
                    class="iyb-gauge__hub"
                    cx="100" cy="100" r="5"
                    :fill="getRiskScoreColor(iybScore)"
                  />
                  <circle class="iyb-gauge__hub-ring" cx="100" cy="100" r="8" :stroke="getRiskScoreColor(iybScore)" />
                </svg>
              </div>
              <div class="iyb-gauge__score">
                <div class="iyb-gauge__value" :style="{ color: getRiskScoreColor(iybScore) }">
                  {{ iybScore.toFixed(2) }}
                </div>
                <div class="text-caption text--secondary">{{ tp('outOf100') }}</div>
              </div>
              <div class="iyb-gauge-legend d-flex justify-space-between mt-1 px-2">
                <span class="iyb-gauge-legend__item iyb-gauge-legend__item--poor">{{ $t('dueDiligence.iyb.highRiskRange') }}</span>
                <span class="iyb-gauge-legend__item iyb-gauge-legend__item--medium">{{ $t('dueDiligence.iyb.mediumRiskRange') }}</span>
                <span class="iyb-gauge-legend__item iyb-gauge-legend__item--good">{{ $t('dueDiligence.iyb.lowRiskRange') }}</span>
              </div>
            </div>
  
            <!-- Indicator breakdown — lives inside Phase 2 -->
            <v-expand-transition>
              <div v-if="showBreakdown && hasIndicatorDetails" class="iyb-indicator-section mt-4">
                <div class="d-flex align-center mb-2">
                  <v-icon small color="primary" class="mr-2">mdi-chart-box-outline</v-icon>
                  <span class="text-caption font-weight-bold text-uppercase" style="color:#444; letter-spacing:0.06em;">
                    {{ $t('dueDiligence.iyb.indicatorBreakdownTitle') }}
                  </span>
                </div>
                <IYBIndicatorBreakdown
                  hide-header
                  :regency-name="iybData.regencyName"
                  :eudr-risk-status="iybComputedRiskStatus"
                  :indicators="iybData.indicators"
                  :data-year="iybData.dataYear"
                  :imported-at="iybData.importedAt"
                />
              </div>
            </v-expand-transition>
          </div>
  
          <div v-else class="iyb-empty-state d-flex align-center pa-3">
            <v-icon color="grey lighten-1" size="36" class="mr-3">mdi-database-off-outline</v-icon>
            <div>
              <div class="text-body-2 font-weight-medium grey--text text--darken-1">{{ tp('noIybDataForRegency') }}</div>
              <div class="text-caption grey--text">{{ tp('importIybToComplete') }}</div>
            </div>
          </div>
        </div>
  
        <!-- ═══════════════════════════════════════════════════════════
             PALM OIL ONLY — Phase 3: Fire Prevention + Phase 4: Satellite Moisture
             ═══════════════════════════════════════════════════════════ -->
        <template v-if="isPalmOil">
  
          <!-- Phase connector (Phase 2 → Phase 3) -->
          <div class="phase-connector mb-3">
            <div class="phase-connector__line"></div>
            <v-icon small color="grey lighten-1">mdi-chevron-down</v-icon>
            <div class="phase-connector__line"></div>
          </div>
  
          <!-- Phase 3: Fire Prevention Check -->
          <div class="phase-card mb-3" :style="phaseTopBorderStyle(firePhaseBorderColor)">
            <div class="phase-card__header d-flex align-center justify-space-between mb-3">
              <div class="d-flex align-center">
                <div class="phase-step-badge mr-2" :style="phaseStepBadgeStyle(firePhaseBorderColor)">3</div>
                <div>
                  <div class="text-caption font-weight-bold text-uppercase phase-card__title">
                    {{ tp('palmOilFirePreventionCheck') }}
                  </div>
                  <div class="text-caption text--secondary">
                    {{ tp('palmOilFirePreventionCheckDesc') }}
                  </div>
                </div>
              </div>
              <div v-if="firePreventionStatus" class="d-flex align-center phase-summary-pill" :class="firePrevSummaryClass">
                <v-icon small class="mr-1">{{ firePrevSummaryIcon }}</v-icon>
                <span class="font-weight-bold phase-summary-pill__label">{{ firePrevSummaryLabel }}</span>
              </div>
            </div>
  
            <!-- Indicator value available -->
            <div v-if="firePreventionIndicatorValue !== null">
  
              <!-- Value row -->
              <div class="d-flex align-center justify-space-between pa-3 mb-3" :style="firePrevCardStyle">
                <div class="d-flex align-center">
                  <div class="fire-prev-icon-wrap mr-2" :style="{ background: firePrevIconBg }">
                    <v-icon small :color="firePrevIconColor">mdi-fire</v-icon>
                  </div>
                  <div>
                    <div class="text-caption font-weight-bold">{{ $t('dueDiligence.iyb.indicators.forestLandFires') }}</div>
                    <div class="text-caption text--secondary">{{ tp('iybIndicatorScale') }}</div>
                  </div>
                </div>
                <div class="text-right fire-prev-score-col">
                  <div class="font-weight-bold text-body-1" :style="{ color: firePrevIconColor }">
                    {{ firePreventionIndicatorValue.toFixed(1) }}<span class="text-caption font-weight-normal text--secondary">&thinsp;/&thinsp;100</span>
                  </div>
                  <v-chip small :class="firePrevChipClass" class="fire-prev-risk-chip mt-1">{{ firePrevChipLabel }}</v-chip>
                </div>
              </div>
  
              <!-- RED → BLOCKED -->
              <div v-if="firePreventionStatus === 'red'" class="pa-3"
                style="background:#fff0f0; border:1.5px solid #7B0000; border-radius:8px;">
                <div class="d-flex align-start">
                  <v-icon small color="#7B0000" class="mr-2 mt-1">mdi-close-octagon</v-icon>
                  <div>
                    <div class="text-caption font-weight-bold mb-1" style="color:#7B0000;">
                      {{ tp('palmOilDdsBlocked') }}
                    </div>
                    <div class="text-caption text--secondary">
                      {{ tp('palmOilDdsBlockedDesc') }}
                    </div>
                  </div>
                </div>
              </div>
  
              <!-- GREEN / YELLOW → PASSES -->
              <div v-else class="pa-3"
                :style="firePreventionStatus === 'green'
                  ? 'background:#f8fff8; border:1px solid #a5d6a7; border-radius:8px;'
                  : 'background:#FFF7EC; border:1px solid #FFE0B2; border-radius:8px;'">
                <div class="d-flex align-start">
                  <v-icon small :color="firePreventionStatus === 'green' ? '#489E16' : '#FFA826'" class="mr-2 mt-1">
                    mdi-check-circle
                  </v-icon>
                  <div>
                    <div class="text-caption font-weight-bold mb-1"
                      :style="{ color: firePreventionStatus === 'green' ? '#489E16' : '#FFA826' }">
                      {{ tp('firePreventionCheckPassed') }}
                    </div>
                    <div class="text-caption text--secondary">
                      {{ firePreventionStatus === 'green'
                        ? tp('firePreventionCheckPassedGreen')
                        : tp('firePreventionCheckPassedYellow') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
  
            <!-- No indicator data -->
            <div v-else class="d-flex align-center pa-3"
              style="background:#f9f9f9; border-radius:8px; border:1px dashed #e0e0e0;">
              <v-icon color="grey lighten-1" class="mr-2">mdi-database-off-outline</v-icon>
              <div class="text-caption grey--text">
                {{ tp('noFireIndicatorData') }}
              </div>
            </div>
          </div>
  
          <!-- 
          Phase 3b: Satellite Moisture Analysis (hidden — enable via showSatelliteMoisturePhase)
          <template v-if="showSatelliteMoisturePhase && firePreventionStatus && firePreventionStatus !== 'red'">

            Phase connector (Phase 3 → Phase 3b)
            <div class="phase-connector mb-3">
              <div class="phase-connector__line"></div>
              <v-icon small color="grey lighten-1">mdi-chevron-down</v-icon>
              <div class="phase-connector__line"></div>
            </div>

            <div class="phase-card mb-3" :style="phaseTopBorderStyle(satellitePhaseBorderColor)">
              <div class="phase-card__header d-flex align-center justify-space-between mb-3">
                <div class="d-flex align-center">
                  <div class="phase-step-badge phase-step-badge--satellite mr-2" :style="phaseStepBadgeStyle(satellitePhaseBorderColor)">4</div>
                  <div>
                    <div class="text-caption font-weight-bold text-uppercase phase-card__title">
                      Satellite Moisture Analysis
                    </div>
                    <div class="text-caption text--secondary">Soil/vegetation moisture — hydrological stress proxy</div>
                  </div>
                </div>
                <div v-if="moistureAnalysisStatus !== 'analyzing'"
                  class="d-flex align-center phase-summary-pill"
                  :class="!moistureAnalysisStatus ? 'phase-summary-pill--upcoming'
                    : moistureAnalysisStatus === 'low_risk' ? 'phase-summary-pill--clear'
                    : 'phase-summary-pill--warn'">
                  <v-icon x-small class="mr-1">{{
                    !moistureAnalysisStatus ? 'mdi-progress-clock'
                      : moistureAnalysisStatus === 'low_risk' ? 'mdi-check-circle'
                      : 'mdi-alert-circle'
                  }}</v-icon>
                  <span class="text-caption font-weight-bold">{{
                    !moistureAnalysisStatus ? 'Request Pending'
                      : moistureAnalysisStatus === 'low_risk' ? 'No Material Risk'
                      : 'Material Risk'
                  }}</span>
                </div>
              </div>

              Satellite disclaimer 
              <div class="pa-3 mb-3 d-flex align-start satellite-disclaimer">
                <v-icon small color="#1976D2" class="mr-2 mt-1">mdi-satellite-variant</v-icon>
                <div>
                  <div class="text-caption font-weight-bold mb-1" style="color:#1565C0;">
                    Satellite-based Risk Proxy — Not Direct Groundwater Measurement
                  </div>
                  <div class="text-caption" style="color:#1976D2; line-height:1.5;">
                    This analysis uses satellite-derived soil and vegetation moisture indices as a proxy for hydrological
                    stress, low soil moisture, and peatland/fire-risk conditions. It is <strong>not</strong> a direct
                    groundwater or hydrological measurement.
                  </div>
                </div>
              </div>

              Analysis runner — always visible 
              <div class="mb-3" style="border:1px solid #e0e0e0; border-radius:10px; background:#fafafa;">
                <div class="d-flex align-center justify-space-between pa-3">
                  <div class="d-flex align-center">
                    <v-icon small color="#0288D1" class="mr-2">mdi-satellite-uplink</v-icon>
                    <div>
                      <div class="text-caption font-weight-bold" style="color:#0288D1;">Satellite Moisture Analysis</div>
                      <div class="text-caption text--secondary">
                        {{ moistureAnalysisStatus === 'analyzing'
                          ? 'Requesting satellite data — please wait...'
                          : moistureAnalysisStatus
                            ? 'Analysis complete — results below'
                            : 'Click Request Analysis to initiate satellite moisture assessment' }}
                      </div>
                    </div>
                  </div>
                  <div class="d-flex align-center" style="gap:8px;">
                    <v-progress-circular v-if="moistureAnalysisStatus === 'analyzing'"
                      indeterminate color="primary" size="20" width="2" />
                    <v-chip v-else-if="moistureAnalysisStatus" x-small
                      :class="moistureAnalysisStatus === 'low_risk' ? 'green-button' : 'orange-button'">
                      <v-icon x-small class="mr-1">{{ moistureAnalysisStatus === 'low_risk' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
                      Complete
                    </v-chip>
                    <v-btn small depressed
                      :color="moistureAnalysisStatus === 'analyzing' ? '' : moistureAnalysisStatus ? '' : 'primary'"
                      :loading="moistureAnalysisStatus === 'analyzing'"
                      :disabled="moistureAnalysisStatus === 'analyzing'"
                      @click="runMoistureAnalysis">
                      <v-icon small :style="{color: '#ffffff !important'}" class="mr-1">mdi-satellite-uplink</v-icon>
                      {{ moistureAnalysisStatus === 'analyzing' ? 'Requesting...' : moistureAnalysisStatus ? 'Re-run' : 'Request Analysis' }}
                    </v-btn>
                  </div>
                </div>
              </div>

              Results (only shown after analysis completes)
              <template v-if="moistureAnalysisStatus && moistureAnalysisStatus !== 'analyzing'">

              Low Risk result
              <div v-if="moistureAnalysisStatus === 'low_risk'">
                <div class="pa-3 d-flex align-start mb-3"
                  style="background:#f8fff8; border:1px solid #C1FFE7; border-radius:8px;">
                  <v-icon small color="#489E16" class="mr-2 mt-1">mdi-check-circle</v-icon>
                  <div>
                    <div class="text-caption font-weight-bold mb-1" style="color:#489E16;">
                      No Material Risk Detected
                    </div>
                    <div class="text-caption text--secondary">
                      Satellite analysis indicates no material hydrological stress, low-moisture conditions,
                      or elevated peatland/fire risk at this farm location.
                    </div>
                  </div>
                </div>
                Checkpoint gate
                <div class="pa-3 d-flex align-start"
                  style="background:#f4fff4; border:1.5px solid #489E16; border-radius:8px;">
                  <v-icon small color="#489E16" class="mr-2 mt-1">mdi-check-decagram</v-icon>
                  <div>
                    <div class="text-caption font-weight-bold mb-1" style="color:#489E16;">
                      Checkpoint Passed — Proceed to Normal PTSI Submission
                    </div>
                    <div class="text-caption text--secondary">
                      All Palm Oil local compliance checks are satisfied for this farm.
                      The DDS may continue through the standard PTSI submission flow.
                    </div>
                  </div>
                </div>
              </div>

              Material Risk result
              <div v-else-if="moistureAnalysisStatus === 'material_risk'">
                <div class="pa-3 d-flex align-start mb-3"
                  style="background:#FFF7EC; border:1px solid #FFA826; border-radius:8px;">
                  <v-icon small color="#FFA826" class="mr-2 mt-1">mdi-alert-circle</v-icon>
                  <div>
                    <div class="text-caption font-weight-bold mb-1" style="color:#E65100;">
                      Material Risk Detected — Supporting Documentation Required
                    </div>
                    <div class="text-caption text--secondary">
                      Satellite analysis indicates poor moisture / water-related conditions at this location
                      (potential hydrological stress, low soil moisture, or peatland/fire-risk area).
                      Supporting evidence must be submitted and reviewed by PTSI before final DDS approval.
                    </div>
                  </div>
                </div>

                Moisture Evidence Upload
                <div class="pa-3" style="border:1px solid #e0e0e0; border-radius:10px; background:#fff;">
                  <div class="d-flex align-center justify-space-between mb-3">
                    <div class="d-flex align-center">
                      <v-icon small color="primary" class="mr-2">mdi-file-upload-outline</v-icon>
                      <span class="text-caption font-weight-bold text-uppercase" style="color:#444; letter-spacing:0.07em;">
                        Supporting Documentation
                      </span>
                    </div>
                    <v-chip x-small :class="moistureEvidenceFiles.length ? 'green-button' : 'gray-button'">
                      {{ moistureEvidenceFiles.length }} file{{ moistureEvidenceFiles.length !== 1 ? 's' : '' }}
                    </v-chip>
                  </div>

                  Accepted doc types
                  <div class="mb-3 pa-2" style="background:#f5f5f5; border-radius:6px;">
                    <div class="text-caption font-weight-medium mb-2" style="color:#555;">Accepted documentation types:</div>
                    <div class="d-flex flex-wrap" style="gap:4px;">
                      <v-chip v-for="docType in moistureDocTypes" :key="docType" x-small outlined color="grey darken-1">
                        {{ docType }}
                      </v-chip>
                    </div>
                  </div>

                  Drop zone
                  <div
                    class="d-flex flex-column align-center justify-center pa-4 mb-3"
                    :style="isDraggingMoisture
                      ? 'border:2px dashed #1976D2; border-radius:8px; background:#f0f7ff; cursor:pointer;'
                      : 'border:2px dashed #ccc; border-radius:8px; background:#fafafa; cursor:pointer;'"
                    @click="$refs.moistureFileInput.click()"
                    @dragover.prevent="isDraggingMoisture = true"
                    @dragleave="isDraggingMoisture = false"
                    @drop.prevent="onMoistureDrop"
                  >
                    <v-icon size="32" :color="isDraggingMoisture ? 'primary' : 'grey lighten-1'" class="mb-2">mdi-cloud-upload-outline</v-icon>
                    <div class="text-caption text--secondary text-center">
                      <span class="font-weight-medium" style="color:#1976D2;">Click to upload</span> or drag &amp; drop<br>
                      PDF, JPG, PNG, DOCX — max 10 MB each
                    </div>
                    <input ref="moistureFileInput" type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.docx" style="display:none;" @change="onMoistureFilesSelected" />
                  </div>

                  File list
                  <div v-if="moistureEvidenceFiles.length" class="mb-3">
                    <div
                      v-for="(f, i) in moistureEvidenceFiles" :key="i"
                      class="d-flex align-center pa-2 mb-1"
                      style="background:#f5f5f5; border-radius:6px;"
                    >
                      <v-icon small :color="fileIconColor(f.name)" class="mr-2">{{ fileIcon(f.name) }}</v-icon>
                      <div class="flex-grow-1" style="min-width:0;">
                        <div class="text-caption font-weight-medium" style="overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">{{ f.name }}</div>
                        <div class="text-caption text--secondary">{{ formatFileSize(f.size) }}</div>
                      </div>
                      <v-chip x-small class="green-button mr-2">Ready</v-chip>
                      <v-btn icon x-small @click.stop="removeMoistureFile(i)">
                        <v-icon x-small color="grey darken-1">mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </div>

                  PTSI review note
                  <div class="pa-2 mb-3 d-flex align-start" style="background:#fff8e1; border-radius:6px;">
                    <v-icon x-small color="#FFA826" class="mr-1 mt-1">mdi-information</v-icon>
                    <div class="text-caption" style="color:#E65100;">
                      All submitted documents must be reviewed and approved by PTSI before final DDS approval is granted.
                    </div>
                  </div>

                  Submit
                  <v-btn depressed class="orange-button w-auto"
                    :style="!moistureEvidenceFiles.length ? 'opacity:0.45; pointer-events:none;' : ''"
                    @click="submitMoistureEvidence">
                    <v-icon small class="mr-2">mdi-send</v-icon>
                    Submit to PTSI for Review
                  </v-btn>
                </div>
              </div>

              </template>
              end: results section

            </div>
          </template>
          -->
        </template>
        <!-- ═══════════════════════════════════════════════════════════ -->
  
        <!-- DARK RED: removal required, no mitigation pathway -->
        <!-- <div v-if="trafficLight === 'RED' && !showMitigation && !isBlockedByFirePrevention" class="mt-1">
          <div class="pa-3 d-flex align-start"
            style="background:#fff0f0; border:1px solid #7B0000; border-radius:10px;">
            <v-icon small color="#7B0000" class="mr-2 mt-1">mdi-close-octagon</v-icon>
            <div>
              <div class="text-caption font-weight-bold mb-1" style="color:#7B0000;">
                Critical Risk — Farm Must Be Removed
              </div>
              <div class="text-caption text--secondary">
                This farm has been flagged as critical risk. No mitigation pathway is available. It must be removed from this DDS before the report can proceed.
              </div>
            </div>
          </div>
        </div> -->
  
        <!-- Mitigation section for YELLOW / LIGHT RED -->
        <div v-if="(trafficLight === 'YELLOW' || trafficLight === 'RED') && showMitigation && !isBlockedByFirePrevention" class="mt-1">
  
          <!-- Note banner -->
          <!-- <div class="pa-3 d-flex align-start mb-3"
            :style="trafficLight === 'RED'
              ? 'background:#fff5f5; border:1px solid #DA3D3D; border-radius:10px;'
              : 'background:#FFF7EC; border:1px solid #FFA826; border-radius:10px;'"
          >
            <v-icon small :color="trafficLight === 'RED' ? '#DA3D3D' : '#FFA826'" class="mr-2 mt-1">
              mdi-information-outline
            </v-icon>
            <div>
              <div class="text-caption font-weight-bold mb-1"
                :style="trafficLight === 'RED' ? 'color:#DA3D3D;' : 'color:#FFA826;'">
                {{ trafficLight === 'YELLOW' ? 'Mitigation Required' : 'High Risk — Appeal Path Available' }}
              </div>
              <div class="text-caption text--secondary">{{ mitigationNote }}</div>
            </div>
          </div> -->
  
          <!-- Evidence Upload card -->
          <div class="pa-3" style="border:1px solid #e0e0e0; border-radius:10px; background:#fff;">
            <div class="d-flex align-center justify-space-between mb-3">
              <div class="d-flex align-center">
                <v-icon small color="primary" class="mr-2">mdi-file-upload-outline</v-icon>
                <span class="text-caption font-weight-bold text-uppercase" style="color:#444; letter-spacing:0.07em;">
                  {{ tp('mitigationEvidence') }}
                </span>
              </div>
              <v-chip x-small :class="totalMitigationCount ? 'green-button' : 'gray-button'">
                {{ $tc('dueDiligence.iyb.trafficLightPanel.fileCount', totalMitigationCount, { count: totalMitigationCount }) }}
              </v-chip>
            </div>

            <!-- Previously uploaded documents -->
            <div v-if="existingMitigationFiles.length" class="mb-3">
              <div class="text-caption font-weight-medium mitigation-existing__title">{{ tp('uploadedDocuments') }}</div>
              <div
                v-for="file in existingMitigationFiles"
                :key="file.id || file.file_path"
                class="mitigation-existing__file-row"
              >
                <v-icon small :color="fileIconColor(getMitigationFileName(file.file_path))" class="mr-2">
                  {{ fileIcon(getMitigationFileName(file.file_path)) }}
                </v-icon>
                <div class="text-caption text-truncate pr-2 mitigation-existing__file-name">
                  {{ getMitigationFileName(file.file_path) }}
                </div>
                <div class="mitigation-existing__file-actions">
                  <v-btn
                    x-small
                    text
                    color="primary"
                    class="mitigation-existing__view-btn"
                    @click="openExistingMitigationFile(file.file_path)"
                  >
                    {{ tp('view') }}
                  </v-btn>
                  <v-btn
                    v-if="canRemoveExistingMitigationFile(file)"
                    x-small
                    text
                    color="error"
                    class="mitigation-existing__remove-btn"
                    :disabled="mitigationSubmitting"
                    @click="$emit('remove-existing-mitigation', file)"
                  >
                    {{ tp('remove') }}
                  </v-btn>
                </div>
              </div>
            </div>
  
            <!-- Drop zone -->
            <div
              class="d-flex flex-column align-center justify-center pa-4 mb-3"
              :style="isDragging
                ? 'border:2px dashed #1976D2; border-radius:8px; background:#f0f7ff; cursor:pointer;'
                : 'border:2px dashed #ccc; border-radius:8px; background:#fafafa; cursor:pointer;'"
              :class="{ 'mitigation-dropzone--disabled': mitigationSubmitting }"
              @click="!mitigationSubmitting && $refs.fileInput.click()"
              @dragover.prevent="isDragging = true"
              @dragleave="isDragging = false"
              @drop.prevent="onDrop"
            >
              <v-icon size="32" :color="isDragging ? 'primary' : 'grey lighten-1'" class="mb-2">mdi-cloud-upload-outline</v-icon>
              <div class="text-caption text--secondary text-center">
                <span class="font-weight-medium" style="color:#1976D2;">{{ tp('clickToUploadOrDrop') }}</span><br>
                {{ tp('fileTypesMaxSize') }}
              </div>
              <input ref="fileInput" type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.docx" style="display:none;" @change="onFilesSelected" />
            </div>
  
            <!-- Uploaded file list -->
            <div v-if="mitigationFileEntries.length" class="mb-3">
              <div
                v-for="(entry, i) in mitigationFileEntries"
                :key="entry.id"
                class="d-flex align-center pa-2 mb-1"
                style="background:#f5f5f5; border-radius:6px;"
              >
                <v-icon small :color="fileIconColor(entry.name)" class="mr-2">{{ fileIcon(entry.name) }}</v-icon>
                <div class="flex-grow-1" style="min-width:0;">
                  <div class="text-caption font-weight-medium" style="overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">{{ entry.name }}</div>
                  <div class="text-caption text--secondary">{{ formatFileSize(entry.size) }}</div>
                  <v-progress-linear
                    v-if="entry.status === 'uploading'"
                    :value="entry.progress"
                    height="4"
                    color="primary"
                    class="mt-1"
                    rounded
                  />
                  <div v-if="entry.status === 'error'" class="text-caption error--text mt-1">
                    {{ entry.error || tp('uploadFailed') }}
                  </div>
                </div>
                <v-progress-circular
                  v-if="entry.status === 'uploading'"
                  :value="entry.progress"
                  size="22"
                  width="2"
                  color="primary"
                  class="mr-2"
                />
                <v-chip v-else-if="entry.status === 'ready'" x-small class="green-button mr-2">{{ tp('ready') }}</v-chip>
                <v-chip v-else-if="entry.status === 'error'" x-small class="red-button mr-2">{{ tp('failed') }}</v-chip>
                <v-btn
                  v-if="entry.status === 'error'"
                  icon
                  x-small
                  class="mr-1"
                  :disabled="mitigationSubmitting"
                  @click.stop="retryMitigationUpload(entry)"
                >
                  <v-icon x-small>mdi-refresh</v-icon>
                </v-btn>
                <v-btn
                  icon
                  x-small
                  :disabled="entry.status === 'uploading' || mitigationSubmitting"
                  @click.stop="removeMitigationFile(i)"
                >
                  <v-icon x-small color="grey darken-1">mdi-close</v-icon>
                </v-btn>
              </div>
            </div>

            <div v-if="hasUploadingMitigationFiles" class="text-caption primary--text mb-2 d-flex align-center">
              <v-progress-circular indeterminate size="14" width="2" color="primary" class="mr-2" />
              {{ $tc('dueDiligence.iyb.trafficLightPanel.uploadingToStorage', uploadingMitigationCount) }}
            </div>
  
            <!-- Submit button -->
            <v-btn
              depressed
              :class="trafficLight === 'RED' ? 'red-button w-auto' : 'orange-button w-auto'"
              :disabled="!canSubmitMitigation"
              :loading="mitigationSubmitting"
              @click="submitEvidence"
            >
              <v-icon small class="mr-2">mdi-send</v-icon>
              {{ mitigationSubmitting ? tp('submittingToPtsi') : tp('submitEvidenceToPtsi') }}
            </v-btn>
          </div>
  
        </div>
  
      </template>
  
    </div>
  </template>
  
  <script>
  import IYBIndicatorBreakdown from '../../iyb-score/components/IYBIndicatorBreakdown.vue'
  import TrafficLightSpatialMap from './TrafficLightSpatialMap.vue'
  import s3UploadService from '@/_services/S3UploadService'
  import {
    IYB_SCORE_MAX,
    IYB_LOW_RISK_MAX,
    IYB_MEDIUM_RISK_MAX,
    IYB_HIGH_RISK_MIN,
    resolveIybRiskScore,
    classifyIybRiskScore,
    normalizeRiskStatusLabel,
    translateIybRiskLabel,
    getIybRiskScoreColor,
    getRiskClassForStatus,
    getRiskIconForStatus,
  } from '@/utils/iybRiskClassification'
  
  export default {
    name: 'IYBTrafficLightPanel',
    components: { IYBIndicatorBreakdown, TrafficLightSpatialMap },
    props: {
      trafficLight: {
        type: String,
        default: null,
        validator: v => ['GREEN', 'YELLOW', 'RED', null].includes(v)
      },
      trafficLightReason: { type: String, default: '' },
      phase1: {
        type: Object,
        default: () => ({ deforestation: false, forestOverlap: false, bufferZone: false })
      },
      iybData: { type: Object, default: null },
      showMitigation: { type: Boolean, default: true },
      farm: { type: Object, default: null },
      farmType: { type: String, default: 'point' },
      mapActive: { type: Boolean, default: true },
      phase1ComingSoon: { type: Boolean, default: true },
      conditionsDocUrl: {
        type: String,
        default: 'https://docs.google.com/document/d/1KWZdz4RcxaiGyk41iSNls-rVdrJiXeOGAWN1UXvN4yg/edit',
      },
      /** 'PALM_OIL' | 'COFFEE' | 'COCOA' | null — drives Phase 3 + 3b for Palm Oil */
      commodity: { type: String, default: null },
      /** Pre-set satellite moisture result for demo: null | 'low_risk' | 'material_risk' */
      demoMoistureStatus: { type: String, default: null },
      mitigationSubmitting: { type: Boolean, default: false },
      diligenceReportId: { type: [Number, String], default: null },
      existingMitigationFiles: { type: Array, default: () => [] },
    },
    data() {
      return {
        showBreakdown: true,
        showSatelliteMoisturePhase: false,
        isDragging: false,
        mitigationFileEntries: [],
        mitigationUploadSeq: 0,
        moistureAnalysisStatus: null, // null | 'analyzing' | 'low_risk' | 'material_risk'
        moistureEvidenceFiles: [],
        isDraggingMoisture: false,
      }
    },
    watch: {
      demoMoistureStatus: {
        immediate: true,
        handler() {
          // only store the target — don't auto-apply so the user must click "Request Analysis"
          this.moistureAnalysisStatus = null
        },
      },
      iybData() {
        this.moistureAnalysisStatus = null
        this.moistureEvidenceFiles = []
      },
      mapActive(isOpen) {
        if (!isOpen) {
          this.mitigationFileEntries = []
          this.isDragging = false
        }
      },
    },
    computed: {
      readyMitigationCount() {
        return this.mitigationFileEntries.filter((entry) => entry.status === 'ready').length
      },
      totalMitigationCount() {
        return this.existingMitigationFiles.length + this.readyMitigationCount
      },
      uploadingMitigationCount() {
        return this.mitigationFileEntries.filter((entry) => entry.status === 'uploading').length
      },
      hasUploadingMitigationFiles() {
        return this.uploadingMitigationCount > 0
      },
      canSubmitMitigation() {
        return this.readyMitigationCount > 0
          && !this.hasUploadingMitigationFiles
          && !this.mitigationSubmitting
      },
      mitigationUploadFolder() {
        const reportId = Number(this.diligenceReportId)
        return reportId
          ? `risk_mitigation_files/dds_${reportId}`
          : 'risk_mitigation_files'
      },
      hasIndicatorDetails() {
        const ind = this.iybData && this.iybData.indicators
        if (!ind) return false
        return Object.values(ind).some(v => typeof v === 'number')
      },
      // IYB composite score on 0–100 scale (higher = better local compliance).
      iybScore() {
        if (!this.iybData) return null
        return resolveIybRiskScore({
          compositeScore: this.iybData.compositeScore,
          indicators: this.iybData.indicators,
        })
      },
      iybComputedRiskStatus() {
        if (this.iybScore !== null) return classifyIybRiskScore(this.iybScore)
        return this.iybData ? normalizeRiskStatusLabel(this.iybData.eudrRiskStatus || 'N/A') : 'N/A'
      },
      iybComputedRiskStatusLabel() {
        return translateIybRiskLabel(this.iybComputedRiskStatus, (key) => this.$t(key))
      },
      phase1ClearCount() {
        return this.spatialChecks.filter(c => !c.failed && !c.warn).length
      },
      phase1ClearPercent() {
        return Math.round((this.phase1ClearCount / 3) * 100)
      },
      phase1SummaryLabel() {
        const issues = 3 - this.phase1ClearCount
        if (issues === 0) return this.tp('phase1AllClear')
        if (issues === 1) return this.tp('phase1OneIssue')
        return this.tp('phase1IssuesFound', { count: issues })
      },
      phase1SummaryColor() {
        if (this.phase1ClearCount === 3) return '#489E16'
        if (this.phase1ClearCount === 2) return '#FFA826'
        return '#DA3D3D'
      },
      phase1SummaryClass() {
        if (this.phase1ClearCount === 3) return 'phase-summary-pill--clear'
        if (this.phase1ClearCount === 2) return 'phase-summary-pill--warn'
        return 'phase-summary-pill--fail'
      },
      spatialPhaseBorderColor() {
        if (this.phase1ComingSoon) return '#607D8B'
        return this.phase1SummaryColor
      },
      iybPhaseBorderColor() {
        if (this.iybScore !== null) {
          if (this.iybScore >= IYB_HIGH_RISK_MIN) return '#489E16'
          if (this.iybScore > IYB_LOW_RISK_MAX) return '#FFA826'
          return '#DA3D3D'
        }
        if (this.iybData) return this.riskStatusBorderColor(this.iybComputedRiskStatus)
        return '#607D8B'
      },
      firePhaseBorderColor() {
        return this.firePrevIconColor
      },
      satellitePhaseBorderColor() {
        if (!this.moistureAnalysisStatus || this.moistureAnalysisStatus === 'analyzing') return '#607D8B'
        if (this.moistureAnalysisStatus === 'low_risk') return '#489E16'
        if (this.moistureAnalysisStatus === 'material_risk') return '#FFA826'
        return '#607D8B'
      },
      phase1LegendPreview() {
        return [
          { key: 'boundary', label: this.tp('legendFarmBoundary'), color: '#A5D6A7' },
          { key: 'deforestation', label: this.tp('legendDeforestation'), color: '#EF9A9A' },
          { key: 'forest', label: this.tp('legendProtectedForest'), color: '#81C784' },
          { key: 'buffer', label: this.tp('legend100mBuffer'), color: '#FFCC80' },
        ]
      },
      iybGaugeNeedle() {
        return this.gaugePointForScore(this.iybScore === null ? 0 : this.iybScore, 54)
      },
      iybGaugeZones() {
        const highRiskEnd = 180 - (IYB_LOW_RISK_MAX / IYB_SCORE_MAX) * 180
        const mediumEnd = 180 - (IYB_HIGH_RISK_MIN / IYB_SCORE_MAX) * 180
        return [
          { key: 'high', color: '#EF5350', d: this.gaugeArcPath(180, highRiskEnd) },
          { key: 'medium', color: '#FFB74D', d: this.gaugeArcPath(highRiskEnd, mediumEnd) },
          { key: 'low', color: '#66BB6A', d: this.gaugeArcPath(mediumEnd, 0) },
        ]
      },
      iybGaugeTicks() {
        return [0, IYB_LOW_RISK_MAX, IYB_HIGH_RISK_MIN, IYB_SCORE_MAX].map(value => {
          const outer = this.gaugePointForScore(value, 76)
          const inner = this.gaugePointForScore(value, 68)
          const label = this.gaugePointForScore(value, 88)
          return {
            value,
            label: String(Math.round(value)),
            x1: inner.x, y1: inner.y,
            x2: outer.x, y2: outer.y,
            lx: label.x, ly: label.y,
          }
        })
      },
      spatialChecks() {
        const checks = [
          {
            key: 'deforestation',
            label: this.tp('spatialDeforestationLabel'),
            shortLabel: this.tp('spatialDeforestationShort'),
            hint: this.tp('spatialDeforestationHint'),
            icon: 'mdi-tree',
            failed: this.phase1.deforestation,
            warn: false,
          },
          {
            key: 'forestOverlap',
            label: this.tp('spatialForestOverlapLabel'),
            shortLabel: this.tp('spatialForestOverlapShort'),
            hint: this.tp('spatialForestOverlapHint'),
            icon: 'mdi-pine-tree',
            failed: this.phase1.forestOverlap,
            warn: false,
          },
          {
            key: 'bufferZone',
            label: this.tp('spatialBufferLabel'),
            shortLabel: this.tp('spatialBufferShort'),
            hint: this.tp('spatialBufferHint'),
            icon: 'mdi-ruler',
            failed: false,
            warn: this.phase1.bufferZone,
          },
        ]

        return checks.map(check => {
          const isFail = check.failed
          const isWarn = check.warn
          const chipLabel = isFail
            ? (check.key === 'deforestation' ? this.tp('chipDetected') : this.tp('chipOverlap'))
            : (isWarn ? this.tp('chipWithin100m') : this.tp('chipClear'))
          let tooltipDetail = ''

          if (check.key === 'deforestation') {
            tooltipDetail = isFail
              ? this.tp('spatialDeforestationFail')
              : this.tp('spatialDeforestationPass')
          } else if (check.key === 'forestOverlap') {
            tooltipDetail = isFail
              ? this.tp('spatialForestOverlapFail')
              : this.tp('spatialForestOverlapPass')
          } else {
            tooltipDetail = isWarn
              ? this.tp('spatialBufferWarn')
              : this.tp('spatialBufferPass')
          }

          return {
            ...check,
            chipLabel,
            tooltipTitle: this.tp('phase1TooltipTitle', { label: check.shortLabel }),
            tooltipStatus: this.tp('checkpointResult', { result: chipLabel }),
            tooltipDetail,
            statusIcon: isFail ? 'mdi-close-circle' : (isWarn ? 'mdi-alert-circle' : 'mdi-check-circle'),
            tileClass: isFail ? 'spatial-check-tile--fail' : (isWarn ? 'spatial-check-tile--warn' : 'spatial-check-tile--clear'),
            iconBg: isFail ? '#ffebee' : (isWarn ? '#fff8e1' : '#e8f5e9'),
            iconColor: isFail ? '#DA3D3D' : (isWarn ? '#FFA826' : '#489E16'),
            dotColor: isFail ? '#DA3D3D' : (isWarn ? '#FFA826' : '#489E16'),
            chipColor: isFail ? '#DA3D3D' : (isWarn ? '#E65100' : '#2E7D32'),
            chipBg: isFail ? '#ffcdd2' : (isWarn ? '#ffe0b2' : '#c8e6c9'),
          }
        })
      },
      iybComplianceTooltipSummary() {
        const regency = this.iybData?.regencyName || this.tp('unknownRegencyLower')
        if (this.iybScore === null) {
          return this.tp('iybIndexUnavailable', { regency })
        }
        return this.tp('iybScoreSummary', {
          regency,
          score: this.iybScore,
          status: this.iybComputedRiskStatusLabel,
        })
      },
      iybComplianceTooltipCheckpoints() {
        const lines = []
        const regency = this.iybData?.regencyName
        if (regency) lines.push(this.tp('regencyAssessed', { regency }))
        if (this.iybScore !== null) {
          lines.push(this.tp('compositeIybScore', { score: this.iybScore }))
          if (this.iybScore >= IYB_HIGH_RISK_MIN) {
            lines.push(this.tp('scoreGreenFastTrack', { min: IYB_HIGH_RISK_MIN }))
          } else if (this.iybScore > IYB_LOW_RISK_MAX) {
            lines.push(this.tp('scoreYellowMitigation', {
              min: IYB_LOW_RISK_MAX + 0.01,
              max: IYB_MEDIUM_RISK_MAX,
            }))
          } else {
            lines.push(this.tp('scoreRedMandatory', { max: IYB_LOW_RISK_MAX }))
          }
        } else if (this.iybData?.eudrRiskStatus) {
          lines.push(this.tp('regionalRiskStatus', {
            status: translateIybRiskLabel(this.iybData.eudrRiskStatus, (key) => this.$t(key)),
          }))
        }
        if (this.hasIndicatorDetails) {
          lines.push(this.tp('indicatorBreakdownAvailable'))
        }
        return lines.length ? lines : [this.tp('localComplianceNotLoaded')]
      },
      mitigationNote() {
        if (this.trafficLight === 'YELLOW') {
          return this.tp('mitigationNoteYellow')
        }
        return this.tp('mitigationNoteRed')
      },
  
      // ── Palm Oil Phase 3 ─────────────────────────────────────────
      isPalmOil() {
        return (this.commodity || '').toUpperCase().replace(/[\s_-]/g, '') === 'PALMOIL'
      },
      firePreventionIndicatorValue() {
        const ind = this.iybData && this.iybData.indicators
        if (!ind || ind.ind2_forestLandFires == null) return null
        return ind.ind2_forestLandFires
      },
      // 'green' (≥60) | 'yellow' (40–60) | 'red' (≤40) | null
      firePreventionStatus() {
        const v = this.firePreventionIndicatorValue
        if (v === null) return null
        if (v >= 60) return 'green'
        if (v > 40) return 'yellow'
        return 'red'
      },
      isBlockedByFirePrevention() {
        return this.isPalmOil && this.firePreventionStatus === 'red'
      },
      firePrevSummaryClass() {
        if (this.firePreventionStatus === 'green')  return 'phase-summary-pill--clear'
        if (this.firePreventionStatus === 'yellow') return 'phase-summary-pill--warn'
        if (this.firePreventionStatus === 'red')    return 'phase-summary-pill--fail'
        return ''
      },
      firePrevSummaryIcon() {
        if (this.firePreventionStatus === 'green')  return 'mdi-check-circle'
        if (this.firePreventionStatus === 'yellow') return 'mdi-alert-circle'
        if (this.firePreventionStatus === 'red')    return 'mdi-close-octagon'
        return 'mdi-help-circle'
      },
      firePrevSummaryLabel() {
        if (this.firePreventionStatus === 'green')  return this.tp('fireRiskLow')
        if (this.firePreventionStatus === 'yellow') return this.tp('fireRiskMedium')
        if (this.firePreventionStatus === 'red')    return this.tp('blocked')
        return this.$t('dueDiligence.iyb.notAvailable')
      },
      firePrevIconColor() {
        if (this.firePreventionStatus === 'green')  return '#489E16'
        if (this.firePreventionStatus === 'yellow') return '#FFA826'
        if (this.firePreventionStatus === 'red')    return '#DA3D3D'
        return '#9E9E9E'
      },
      firePrevIconBg() {
        if (this.firePreventionStatus === 'green')  return '#e8f5e9'
        if (this.firePreventionStatus === 'yellow') return '#fff8e1'
        if (this.firePreventionStatus === 'red')    return '#ffebee'
        return '#f5f5f5'
      },
      firePrevCardStyle() {
        if (this.firePreventionStatus === 'green')  return 'background:#f8fff8; border:1px solid #C1FFE7; border-radius:8px;'
        if (this.firePreventionStatus === 'yellow') return 'background:#FFF7EC; border:1px solid #FFE0B2; border-radius:8px;'
        if (this.firePreventionStatus === 'red')    return 'background:#fff0f0; border:1px solid #FFCDD2; border-radius:8px;'
        return ''
      },
      firePrevChipClass() {
        if (this.firePreventionStatus === 'green')  return 'green-button'
        if (this.firePreventionStatus === 'yellow') return 'orange-button'
        return 'red-button'
      },
      firePrevChipLabel() {
        if (this.firePreventionStatus === 'green')  return this.tp('lowRiskGte60')
        if (this.firePreventionStatus === 'yellow') return this.tp('mediumRisk')
        return this.tp('highRiskLte40')
      },
      conditionsDocLabel() {
        if (this.isPalmOil) return this.tp('conditionsDocPalmOil')
        return this.tp('conditionsDocCoffeeCocoa')
      },
      effectiveConditionsDocUrl() {
        if (this.isPalmOil) {
          return 'https://docs.google.com/document/d/1-YIsTPCVDnWTyIXRfE01gD7TyjVmKC84zX6EA6K64gM/edit'
        }
        return this.conditionsDocUrl
      },
      moistureDocTypes() {
        return [
          'Field evidence',
          'Hydrological records',
          'Water management evidence',
          'Canal blocking evidence',
          'Geotagged photos',
          'PTSI-defined documents',
        ]
      },
    },
    methods: {
      tp(key, params) {
        return this.$t(`dueDiligence.iyb.trafficLightPanel.${key}`, params)
      },
      riskStatusBorderColor(status) {
        const label = normalizeRiskStatusLabel(status)
        if (label === 'Low Risk') return '#489E16'
        if (label === 'Medium Risk') return '#FFA826'
        if (label === 'High Risk' || label === 'Critical Risk') return '#DA3D3D'
        return '#607D8B'
      },
      phaseTopBorderStyle(color) {
        return { borderTop: `3px solid ${color || '#607D8B'}` }
      },
      phaseStepBadgeStyle(color) {
        return { background: color || '#607D8B' }
      },
      getMitigationFileName(filePath) {
        if (!filePath) return this.tp('mitigationDocument')
        const parts = String(filePath).split('/')
        return parts[parts.length - 1] || this.tp('mitigationDocument')
      },
      canRemoveExistingMitigationFile(file) {
        if (!file) return false
        const fileId = Number(file.id)
        return Number.isInteger(fileId) && fileId > 0
      },
      async openExistingMitigationFile(filePath) {
        if (!filePath) return
        try {
          const fileKey = filePath.split('.com/')[1] || filePath
          const viewUrl = await s3UploadService.getFileUrl(fileKey)
          window.open(viewUrl, '_blank')
        } catch (error) {
          console.error('Error opening mitigation file', error)
          this.$notify?.({ text: this.tp('couldNotOpenMitigationDoc'), type: 'error' })
        }
      },
      getRiskClass(status) {
        return getRiskClassForStatus(status)
      },
      getRiskIcon(status) {
        return getRiskIconForStatus(status)
      },
      getRiskScoreColor(score) {
        return getIybRiskScoreColor(score)
      },
      gaugePointForScore(score, radius) {
        const angleDeg = 180 - (score / IYB_SCORE_MAX) * 180
        const rad = (angleDeg * Math.PI) / 180
        const cx = 100
        const cy = 100
        return {
          x: cx + radius * Math.cos(rad),
          y: cy - radius * Math.sin(rad),
        }
      },
      gaugeArcPath(startDeg, endDeg) {
        const r = 72
        const startPt = this.pointOnGaugeArc(startDeg, r)
        const endPt = this.pointOnGaugeArc(endDeg, r)
        return `M ${startPt.x} ${startPt.y} A ${r} ${r} 0 0 1 ${endPt.x} ${endPt.y}`
      },
      pointOnGaugeArc(angleDeg, radius) {
        const rad = (angleDeg * Math.PI) / 180
        const cx = 100
        const cy = 100
        return {
          x: cx + radius * Math.cos(rad),
          y: cy - radius * Math.sin(rad),
        }
      },
      onFilesSelected(e) {
        const files = Array.from(e.target.files || [])
        this.addFiles(files)
        e.target.value = ''
      },
      onDrop(e) {
        this.isDragging = false
        this.addFiles(Array.from(e.dataTransfer.files || []))
      },
      addFiles(files) {
        const MAX = 10 * 1024 * 1024
        files.forEach((file) => {
          if (file.size > MAX) {
            this.$notify?.({ text: this.tp('fileExceedsLimit', { name: file.name }), type: 'warning' })
            return
          }
          const duplicate = this.mitigationFileEntries.find(
            (entry) => entry.name === file.name && entry.size === file.size,
          )
          if (duplicate) return

          const entry = {
            id: `mit-${Date.now()}-${this.mitigationUploadSeq += 1}`,
            file,
            name: file.name,
            size: file.size,
            status: 'uploading',
            progress: 0,
            path: null,
            error: null,
          }
          this.mitigationFileEntries.push(entry)
          this.uploadMitigationEntry(entry)
        })
      },
      async uploadMitigationEntry(entry) {
        entry.status = 'uploading'
        entry.progress = 0
        entry.error = null
        try {
          const result = await s3UploadService.singleUpload(
            entry.file,
            this.mitigationUploadFolder,
            (progress) => {
              entry.progress = progress
            },
          )
          entry.path = result?.fullPath || null
          if (!entry.path) throw new Error('Upload did not return a file path')
          entry.status = 'ready'
          entry.progress = 100
        } catch (error) {
          console.error('Mitigation file upload failed', error)
          entry.status = 'error'
          entry.error = error?.message || this.tp('uploadFailed')
        }
      },
      retryMitigationUpload(entry) {
        this.uploadMitigationEntry(entry)
      },
      removeMitigationFile(index) {
        this.mitigationFileEntries.splice(index, 1)
      },
      submitEvidence() {
        const paths = this.mitigationFileEntries
          .filter((entry) => entry.status === 'ready' && entry.path)
          .map((entry) => entry.path)
        if (!paths.length) return
        this.$emit('submit-mitigation', paths)
      },
      formatFileSize(bytes) {
        if (bytes < 1024) return bytes + ' B'
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
      },
      fileIcon(name) {
        const ext = (name || '').split('.').pop().toLowerCase()
        if (ext === 'pdf') return 'mdi-file-pdf-box'
        if (['jpg', 'jpeg', 'png'].includes(ext)) return 'mdi-file-image'
        if (ext === 'docx') return 'mdi-file-word'
        return 'mdi-file-outline'
      },
      fileIconColor(name) {
        const ext = (name || '').split('.').pop().toLowerCase()
        if (ext === 'pdf') return '#DA3D3D'
        if (['jpg', 'jpeg', 'png'].includes(ext)) return '#1976D2'
        if (ext === 'docx') return '#1565C0'
        return '#757575'
      },
  
      // ── Palm Oil Satellite Moisture Analysis ─────────────────────
      runMoistureAnalysis() {
        this.moistureAnalysisStatus = 'analyzing'
        setTimeout(() => {
          this.moistureAnalysisStatus = this.demoMoistureStatus || 'low_risk'
        }, 3000)
      },
      onMoistureFilesSelected(e) {
        this.addMoistureFiles(Array.from(e.target.files || []))
        e.target.value = ''
      },
      onMoistureDrop(e) {
        this.isDraggingMoisture = false
        this.addMoistureFiles(Array.from(e.dataTransfer.files || []))
      },
      addMoistureFiles(files) {
        const MAX = 10 * 1024 * 1024
        files.forEach(f => {
          if (f.size > MAX) return
          if (!this.moistureEvidenceFiles.find(x => x.name === f.name && x.size === f.size)) {
            this.moistureEvidenceFiles.push(f)
          }
        })
      },
      removeMoistureFile(i) {
        this.moistureEvidenceFiles.splice(i, 1)
      },
      submitMoistureEvidence() {
        this.$emit('submit-moisture-evidence', this.moistureEvidenceFiles)
      },
    }
  }
  </script>
  
  <style scoped>
  .conditions-doc-link {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
  }
  
  .conditions-doc-link__anchor {
    color: #1565c0;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
  }
  
  .conditions-doc-link__anchor:hover {
    text-decoration: underline;
  }
  
  .phase-card {
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    background: #fff;
    padding: 14px;
  }
  
  .phase-summary-pill--upcoming {
    background: #ECEFF1;
    border: 1px solid #CFD8DC;
    color: #546E7A;
    padding: 6px 12px;
    border-radius: 20px;
  }
  
  .phase-upcoming-feature {
    position: relative;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid #ECEFF1;
  }
  
  .phase-upcoming-feature__preview {
    pointer-events: none;
    user-select: none;
  }
  
  .phase-upcoming-feature__map {
    position: relative;
    height: 200px;
    background: linear-gradient(160deg, #ECEFF1 0%, #F5F7FA 45%, #E8EAF0 100%);
    overflow: hidden;
  }
  
  .phase-upcoming-feature__map-grid {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(176, 190, 197, 0.2) 1px, transparent 1px),
      linear-gradient(90deg, rgba(176, 190, 197, 0.2) 1px, transparent 1px);
    background-size: 28px 28px;
    opacity: 0.6;
  }
  
  .phase-upcoming-feature__map-content {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    z-index: 1;
  }
  
  .phase-upcoming-feature__farm-name {
    font-size: 11px;
    font-weight: 600;
    color: #90A4AE;
    max-width: 80%;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .phase-upcoming-feature__legend {
    position: absolute;
    left: 10px;
    bottom: 10px;
    right: 10px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px 10px;
    z-index: 1;
  }
  
  .phase-upcoming-feature__legend-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 9px;
    font-weight: 600;
    color: #90A4AE;
    background: rgba(255, 255, 255, 0.75);
    padding: 2px 6px;
    border-radius: 4px;
  }
  
  .phase-upcoming-feature__legend-swatch {
    width: 8px;
    height: 8px;
    border-radius: 2px;
    flex-shrink: 0;
    opacity: 0.65;
  }
  
  .phase-upcoming-feature__tiles {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    padding: 10px;
    background: #FAFBFC;
    border-top: 1px solid #ECEFF1;
  }
  
  .phase-upcoming-feature__tile {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 12px 8px;
    background: #F5F7FA;
    border: 1px dashed #CFD8DC;
    border-radius: 10px;
    min-height: 72px;
  }
  
  .phase-upcoming-feature__tile-label {
    font-size: 10px;
    font-weight: 600;
    color: #90A4AE;
    text-align: center;
    line-height: 1.2;
  }
  
  .phase-upcoming-feature__overlay {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(250, 251, 252, 0.82);
    backdrop-filter: blur(2px);
  }
  
  .phase-upcoming-feature__card {
    text-align: center;
    max-width: 300px;
    padding: 4px 16px;
  }
  
  .phase-upcoming-feature__pill {
    display: inline-block;
    padding: 4px 12px;
    margin-bottom: 10px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #455A64;
    background: #fff;
    border: 1px solid #CFD8DC;
    border-radius: 20px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  }
  
  .phase-upcoming-feature__title {
    font-size: 15px;
    font-weight: 700;
    color: #37474F;
    line-height: 1.3;
    margin-bottom: 6px;
  }
  
  .phase-upcoming-feature__desc {
    font-size: 12px;
    line-height: 1.45;
    color: #78909C;
  }
  
  .phase-card__title {
    color: #333;
    letter-spacing: 0.06em;
  }
  
  .phase-step-badge {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #1976D2;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  
  .phase-summary-pill {
    padding: 4px 10px 4px 4px;
    border-radius: 20px;
    background: #f5f5f5;
  }
  
  .phase-summary-pill--clear {
    background: #e8f5e9;
    color: #2E7D32;
  }
  
  .phase-summary-pill--warn {
    background: #fff8e1;
    color: #E65100;
  }
  
  .phase-summary-pill--fail {
    background: #ffebee;
    color: #C62828;
  }
  
  .spatial-check-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
  }
  
  .spatial-check-tile {
    position: relative;
    text-align: center;
    padding: 12px 8px 10px;
    border-radius: 10px;
    border: 1.5px solid #e8e8e8;
    background: #fafafa;
    transition: border-color 0.2s;
  }
  
  .spatial-check-tile--tooltip {
    cursor: help;
    width: 100%;
  }
  
  .iyb-risk-badge--tooltip {
    cursor: help;
  }
  
  .iyb-tooltip-checkpoints li {
    margin-bottom: 4px;
  }
  
  .iyb-tooltip-checkpoints li:last-child {
    margin-bottom: 0;
  }
  
  .spatial-check-tile--clear {
    border-color: #a5d6a7;
    background: #f9fff9;
  }
  
  .spatial-check-tile--warn {
    border-color: #ffcc80;
    background: #fffdf5;
  }
  
  .spatial-check-tile--fail {
    border-color: #ef9a9a;
    background: #fff8f8;
  }
  
  .spatial-check-tile__icon-wrap {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    margin: 0 auto 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .spatial-check-tile__status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin: 0 auto 6px;
  }
  
  .spatial-check-tile__title {
    font-size: 11px;
    font-weight: 700;
    color: #333;
    line-height: 1.2;
    margin-bottom: 6px;
  }
  
  .spatial-check-tile__chip {
    display: inline-flex;
    align-items: center;
    font-size: 10px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 10px;
    margin-bottom: 4px;
  }
  
  .spatial-check-tile__hint {
    font-size: 9px;
    color: #9e9e9e;
    line-height: 1.2;
  }
  
  .phase-connector {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 0 20px;
  }
  
  .phase-connector__line {
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, transparent, #e0e0e0, transparent);
  }
  
  .iyb-visual-panel {
    background: #f9faf9;
    border-radius: 10px;
    padding: 14px;
  }
  
  .regency-pin {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #e3f2fd;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .iyb-risk-badge {
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    font-weight: 700;
    padding: 6px 12px;
    border-radius: 16px;
    white-space: nowrap;
  }
  
  .iyb-score-visual {
    text-align: center;
  }
  
  .iyb-gauge-wrap {
    max-width: 260px;
    margin: 0 auto -10px;
  }
  
  .iyb-gauge {
    width: 100%;
    height: auto;
    display: block;
  }
  
  .iyb-gauge__zone {
    fill: none;
    stroke-width: 13;
    stroke-linecap: round;
  }
  
  .iyb-gauge__tick {
    stroke: #bdbdbd;
    stroke-width: 1.5;
    stroke-linecap: round;
  }
  
  .iyb-gauge__tick-label {
    fill: #757575;
    font-size: 9px;
    font-weight: 600;
  }
  
  .iyb-gauge__needle {
    stroke-width: 2.5;
    stroke-linecap: round;
    filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.15));
  }
  
  .iyb-gauge__hub {
    stroke: #fff;
    stroke-width: 2;
  }
  
  .iyb-gauge__hub-ring {
    fill: none;
    stroke-width: 1.5;
    opacity: 0.35;
  }
  
  .iyb-gauge__score {
    text-align: center;
    padding: 4px 0 2px;
    line-height: 1.2;
  }
  
  .iyb-gauge__value {
    font-size: 32px;
    font-weight: 800;
    line-height: 1.1;
  }
  
  .iyb-gauge-legend {
    max-width: 340px;
    margin: 0 auto;
  }
  
  .iyb-gauge-legend__item {
    font-size: 9px;
    font-weight: 600;
  }
  
  .iyb-gauge-legend__item--poor { color: #C62828; }
  .iyb-gauge-legend__item--medium { color: #E65100; }
  .iyb-gauge-legend__item--good { color: #2E7D32; }
  
  .iyb-indicator-section {
    padding-top: 14px;
    border-top: 1px solid #e8e8e8;
  }
  
  .iyb-empty-state {
    background: #f9f9f9;
    border-radius: 10px;
    border: 1px dashed #e0e0e0;
  }
  
  @media (max-width: 520px) {
    .spatial-check-grid {
      grid-template-columns: 1fr;
    }
  }
  
  /* ── Palm Oil Phase 3 — Fire Prevention ──────────────── */
  .fire-prev-icon-wrap {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .fire-prev-score-col {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: center;
  }

  .fire-prev-risk-chip {
    min-width: 110px !important;
    height: 26px !important;
    font-size: 14px !important;
    font-weight: 600;
    justify-content: center;
    padding: 0 12px !important;
  }

  .fire-prev-risk-chip ::v-deep .v-chip__content {
    font-size: 14px !important;
    line-height: 1.2;
  }

  .phase-summary-pill__label {
    font-size: 14px;
    line-height: 1.2;
  }
  
  /* ── Palm Oil Phase 3b — Satellite Moisture ──────────── */
  .phase-step-badge--satellite {
    font-size: 10px;
    width: 32px;
    height: 32px;
  }
  
  .satellite-disclaimer {
    background: #E3F2FD;
    border: 1px solid #90CAF9;
    border-radius: 8px;
  }

  .mitigation-dropzone--disabled {
    opacity: 0.6;
    pointer-events: none;
  }

  .mitigation-existing__title {
    color: #444;
    margin-bottom: 8px;
  }

  .mitigation-existing__file-row {
    display: flex;
    align-items: center;
    padding: 8px 10px;
    margin-bottom: 6px;
    background: #f5f7ff;
    border: 1px solid #e0e7ff;
    border-radius: 6px;
  }

  .mitigation-existing__file-name {
    flex: 1;
    min-width: 0;
    font-weight: 500;
  }

  .mitigation-existing__file-actions {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: 4px;
  }

  .mitigation-existing__view-btn,
  .mitigation-existing__remove-btn {
    min-width: 0 !important;
    padding: 0 6px !important;
  }
  </style>
  